// ** Modules
import { gql } from '@apollo/client';

// =========================================================================
// 1. BASE LEAF FRAGMENTS (No external fragment dependencies)
// =========================================================================

export const SearchFilter = gql`
	fragment SearchFilter on ComponentSharedSearchFilter {
		__typename
		searchFilterShowCategory
		searchFilterCategoryPlaceholder
		searchFilterShowPeriod
		searchFilterPeriodPlaceholder
		searchFilterShowSearch
		searchFilterSearchPlaceholder
	}
`;

export const RecursivePageLink = gql`
	fragment RecursivePageLink on Page {
		slug
		parent_page {
			slug
			parent_page {
				slug
				parent_page {
					slug
					parent_page {
						slug
						parent_page {
							slug
						}
					}
				}
			}
		}
	}
`;

export const MediaFields = gql`
	fragment MediaFields on UploadFile {
		__typename
		name
		alternativeText
		caption
		formats
		url
		focalPoint
	}
`;

export const FileFields = gql`
	fragment FileFields on DataFile {
		file {
			documentId
			url
			mime
			name
			ext
			size
			updatedAt
		}
	}
`;

export const TextEditor = gql`
	fragment TextEditor on ComponentSharedTextEditor {
		__typename
		blockContent
		id
		variantBody
		variantHeading1
		variantHeading2
		variantHeading3
		variantHeading4
		variantHeading5
		variantHeading6
	}
`;

export const ButtonBlock = gql`
	fragment ButtonBlock on ComponentBlocksButtonLink {
		id
		__typename
		text
		url
		buttonVariant
	}
`;

export const WYSISYGBlock = gql`
	fragment WYSISYGBlock on ComponentBlocksWysiwygBlock {
		id
		__typename
		editorContent
	}
`;

// =========================================================================
// 2. INTERMEDIATE FRAGMENTS (Dependent on base leaf fragments above)
// =========================================================================

export const CtaFields = gql`
	${RecursivePageLink}

	fragment CtaFields on ComponentSharedLinkPage {
		id
		__typename
		label
		href {
			...RecursivePageLink
		}
		hrefExternal
	}
`;

// export const EhrenamtFileFields = gql`
// 	fragment EhrenamtFileFields on UploadFile {
// 		documentId
// 		url
// 		mime
// 		name
// 		ext
// 		size
// 		updatedAt
// 	}
// `;

export const RelevantPersonFields = gql`
	${MediaFields}

	fragment RelevantPersonFields on RelevantPerson {
		documentId
		email
		firstName
		lastName
		mobile
		telephone
		positionLine1
		positionLine2
		positionLine3
		sector
		team
		profilePicture {
			...MediaFields
		}
	}
`;

export const ImageElementContent = gql`
	${MediaFields}

	fragment ImageElementContent on ComponentElementsCarouselImageItem {
		id
		imageTitle
		imageDescription
		imageCaption
		imageItem {
			...MediaFields
		}
	}
`;

// =========================================================================
// 3. LAYOUT BLOCK FRAGMENTS (Dependent on all upper elements)
// =========================================================================

export const GridNavigationBlock = gql`
	${MediaFields}

	fragment GridNavigationBlock on ComponentBlocksGridNavigation {
		id
		__typename
		navGrigColumnCount
		gridItems(pagination: { page: 1, pageSize: 1000 }, sort: "id:asc") {
			__typename
			id
			title
			restrictedToGroups
			gridNavigationItemCtaLabel
			gridNavigationItemExternalUrl
			gridNavigationItemDescription
			gridNavigationItemImage {
				...MediaFields
			}
			url {
				...RecursivePageLink
				pageDescription
				pageHero {
					heroImage {
						...MediaFields
					}
					heroImageDarkMode {
						...MediaFields
					}
				}
			}
		}
	}
`;

export const TextBlock = gql`
	${TextEditor}
	${CtaFields}

	fragment TextBlock on ComponentBlocksTextBlock {
		id
		__typename
		textBlockHeadline
		textBlockAlignment
		textBlockContent {
			...TextEditor
		}
		textBlockCta {
			...CtaFields
		}
	}
`;

export const BildTextBlock = gql`
	${TextEditor}
	${CtaFields}
	${MediaFields}

	fragment BildTextBlock on ComponentBlocksBildTextBlock {
		id
		__typename
		bildTextVariant
		bildTextEyebrow
		bildTextTitle
		bildTextContentVariant
		bildTextCopy
		bildTextImagePosition
		bildTextContent {
			...TextEditor
		}
		bildTextImage {
			...MediaFields
		}
		bildTextImageDarkMode {
			...MediaFields
		}
		bildTextCta {
			...CtaFields
		}
	}
`;

export const PersonBlock = gql`
	${RelevantPersonFields}

	fragment PersonBlock on ComponentBlocksSectionBlock {
		id
		__typename
		background
		sectionTitle
		personBlockContent {
			__typename
			id
			individual {
				...RelevantPersonFields
			}
		}
	}
`;

export const CardsRowBlock = gql`
	${RelevantPersonFields}

	fragment CardsRowBlock on ComponentBlocksCardsRow {
		id
		__typename
		cardsRowSectionTitle
		cardsRowContent {
			__typename
			id
			individual {
				...RelevantPersonFields
			}
		}
	}
`;

export const VideoBlock = gql`
	${MediaFields}

	fragment VideoBlock on ComponentBlocksVideoBlock {
		id
		__typename
		videoSectionTitle
		videoContent {
			__typename
			id
			videoData {
				link
				videoTitle
				videoDescription
				video {
					...MediaFields
				}
			}
		}
	}
`;

export const FileBlock = gql`
	${SearchFilter}
	${FileFields}

	fragment FileBlock on ComponentBlocksFileBlock {
		id
		__typename
		fileBlockSectionTitle
		fileBlockAccessRestriction
		fileBlockLayout
		fileBlockBackground
		fileBlockShowPreviews
		fileBlockAuthenticated
		fileBlockAccessRestriction
		fileBlockSearchFilter {
			...SearchFilter
		}
		fileBlockContent(pagination: { page: 1, pageSize: 1000 }) {
			__typename
			id
			dataFile {
				fileName
				fileDescription
				fileCategory
				fileDate
				fileAuthenticated
				fileAccessRestrictions
				...FileFields
			}
		}
	}
`;

export const PartnerBlock = gql`
	${SearchFilter}
	${MediaFields}

	fragment PartnerBlock on ComponentBlocksPartnerBlock {
		id
		__typename
		partnerFilterTools
		partnerSectionTitle
		partnerSearchFilter {
			...SearchFilter
		}
		partnerContent(pagination: { page: 1, pageSize: 1000 }) {
			partnerData {
				partnerEmail
				partnerName
				partnerDescription
				partnerCategory
				partnerTelephone
				partnerAddress
				partnerUrl
				logo {
					...MediaFields
				}
				partnerTags {
					name
					slug
				}
			}
		}
	}
`;

export const TeamBlock = gql`
	${RelevantPersonFields}
	${SearchFilter}

	fragment TeamBlock on ComponentBlocksTeamBlock {
		id
		__typename
		teams
		teamFilterTools
		teamSectionTitle
		teamContent {
			teamGroup
			teamIndividuals(pagination: { page: 1, pageSize: 1000 }) {
				individual {
					...RelevantPersonFields
				}
			}
		}
		teamSearchFilters {
			...SearchFilter
		}
	}
`;

export const AccordionGroup = gql`
	${TextEditor}
	${MediaFields}
	${FileFields}

	fragment AccordionGroup on ComponentBlocksAccordionGroup {
		id
		__typename
		accordionGroupTitle
		allowDefaultExpanded
		allowMultipleExpanded	
		accordion(pagination: { page: 1, pageSize: 1000 }) {
			id
			expandedAccordionItem
			elementName
			elementType
			accordionElementTypeTextBlock {
				id
				accordionElementTextBlockTextEditorType
				accordionElementTextBlockCopyAccordion
				accordionElementTextBlockContent {
					...TextEditor
				}
				accordionElementTextBlockImage {
					...MediaFields
				}
			}
			accordionElementTypeInnung {
				accordionElementInnung {
					innungCity
					innungEmail
					innungName
					innungPostalCode
					innungStreet
					innungTelephone
					innungUrl
					obermeisterCompanyWebsite
					obermeisterCompanyTelephone
					obermeisterCompanyStreet
					obermeisterCompanyPostalCode
					obermeisterCompanyEmail
					obermeisterCompanyCity
					obermeisterCompany
					obermeisterName
					obermeister {
						firstName
						lastName
					}
					innungLogo {
						...MediaFields
					}
				}
			}
			accordionElementFilesAuthenticated
			accordionElementTypeFiles(pagination: { page: 1, pageSize: 1000 }, sort: "id:asc") {
				accordionElementFiles {
					...FileFields
				}
			}
		}
	}
`;

export const FragmentEhrenamtBlock = gql`
	${SearchFilter}

	fragment FragmentEhrenamtBlock on ComponentBlocksEhrenamtBlock {
		id
		__typename
		ehrenamtSearchFilter {
			...SearchFilter
		}
	}
`;

export const GraphicBlock = gql`
	fragment GraphicBlock on ComponentBlocksGraphicBlock {
		id
		__typename
		graphicTitle
		graphicContent(pagination: { page: 1, pageSize: 1000 }) {
			id
			category
			firstValueTitle
			firstValue
			secondValueTitle
			secondValue
			thirdValueTitle
			thirdValue
			fourthValueTitle
			fourthValue
			fifthValueTitle
			fifthValue
		}
	}
`;

export const PodcastBlock = gql`
	fragment PodcastBlock on ComponentBlocksPodcastBlock {
		id
		__typename
		podcastSectionTitle
		podcasts(pagination: { page: 1, pageSize: 1000 }, sort: "publishedAt:desc") {
			documentId
			publishedAt
			embedUrl
		}
	}
`;

export const NewsBlock = gql`
	${SearchFilter}

	fragment NewsBlock on ComponentBlocksNewsBlock {
		id
		__typename
		filterTools
		newsPerPage
		newsSearchFilters {
			...SearchFilter
		}
	}
`;

export const EventsBlock = gql`
	${SearchFilter}

	fragment EventsBlock on ComponentBlocksEvents {
		id
		__typename
		eventsPerPage
		eventsFilterTools
		eventsSearchFilters {
			...SearchFilter
		}
	}
`;

export const ButtonLinkRowBlock = gql`
	${CtaFields}

	fragment ButtonLinkRowBlock on ComponentBlocksButtonLinkRow {
		id
		__typename
		buttonLinkRowListAriaLabel
		linkCard {
			__typename
			id
			title
			description
			active
			visible
			buttonLinkRowCardIsLogin
			buttonLinkRowCardIcon {
				icon
			}
			buttonLinkRowCardLink {
				...CtaFields
			}
		}
	}
`;

export const CarouselBlock = gql`
	${MediaFields}
	${RecursivePageLink}
	${ImageElementContent}

	fragment CarouselBlock on ComponentBlocksCarouselBlock {
		id
		__typename
		carouselShowTitle
		carouselSectionTitle
		carouselType
		mediaTypes
		carouselShowArrowControls
		carouselShowCta
		imageCarouselOnlyImages
		carouselCta {
			label
			hrefExternal
			href {
				...RecursivePageLink
			}
		}
		imageCarouselImages {
			...ImageElementContent
		}
	}
`;

export const ImageGridCard = gql`
	${MediaFields}
	${RecursivePageLink}

	fragment ImageGridCard on ComponentElementsImageGridCard {
		id
		__typename
		imageGridCaption
		image {
			...MediaFields
		}
		pageRelation {
			...CtaFields
		}
	}
`;

export const ImageGridBlock = gql`
	${ImageGridCard}

	fragment ImageGridBlock on ComponentBlocksImageGridBlock {
		id
		__typename
		imageGridSectionTitle
		imageGridContent(pagination: { page: 1, pageSize: 1000 }) {
			...ImageGridCard
		}
	}
`;

export const HeroBlock = gql`
	${MediaFields}
	${RecursivePageLink}

	fragment HeroBlock on ComponentBlocksHero {
		id
		__typename
		heroVariant
		heroImage {
			...MediaFields
		}
		heroImageDarkMode {
			...MediaFields
		}
		showMenu
		heroColorVariant
		eyebrow
		headline
		subline
		copy
		ctaVariant
		linkLabel
		linkHref {
			...RecursivePageLink
		}
		linkHrefExternal
		readingTimeBold
		readingTimeSuffix
	}
`;

export const DashboardOverview = gql`
	${CtaFields}

	fragment DashboardOverview on ComponentDashboardOverview {
		__typename
		dashboardProfile {
			dashboardProfileTitle
			dashboardProfileAllowEdit
		}
		dashboardSections {
			dashboardSectionTitle
			dashboardSectionType
			dashboardSectionMaxItems
			dashboardSectionCollapsible
			dashboardSectionDefaultExpanded
			dashboardSectionCta {
				...CtaFields
			}
		}
	}
`;

export const ImageBlock = gql`
	${ImageElementContent}

	fragment ImageBlock on ComponentBlocksImage {
		id
		__typename
		imageSectionTitle
		# imageContent(pagination: { page: 1, pageSize: 1000 }) {
		imageContent {
			...ImageElementContent
		}
	}
`;

export const AccordionCardItem = gql`
	${TextEditor}

	fragment AccordionCardItem on ComponentElementsAccordionCardItem {
		id
		__typename
		accordionCardIcon {
			icon
		}
		accordionCardTitle
		accordionCardDescription
		accordionCardTextVariant
		accordionCardTextContent
		accordionCardBlockEditor {
			...TextEditor
		}
	}
`;

export const AccordionCards = gql`
	${AccordionCardItem}

	fragment AccordionCards on ComponentBlocksAccordionCards {
		id
		__typename
		accordionCardsSectionTitle
		accordionCardsItems {
			...AccordionCardItem
		}
	}
`;

export const CompetitionCardExtraImage = gql`
	${MediaFields}

	fragment CompetitionCardExtraImage on ComponentElementsCompetitionCardExtraImage {
		id
		__typename
		image {
			...MediaFields
		}
		videoUrl
	}
`;

export const CompetitionCardItem = gql`
	${MediaFields}
	${CompetitionCardExtraImage}

	fragment CompetitionCardItem on ComponentElementsCompetitionCardItem {
		id
		__typename
		competitionCardName
		competitionCardImage {
			...MediaFields
		}
		competitionCardRanking
		competitionCardDescription
		competitionCardFullDescription {
			...TextEditor
		}
		competitionCardTrainingCompany
		competitionCardGuild
		competitionCardMaterial
		competitionCardExtraImages {
			...CompetitionCardExtraImage
		}
		competitionCardCta {
			...CtaFields
		}
	}
`;

export const CompetitionCardsBlock = gql`
	${CompetitionCardItem}

	fragment CompetitionCardsBlock on ComponentBlocksCompetitionCards {
		id
		__typename
		competitionCardsSectionTitle
		competitionCardsContent(pagination: { page: 1, pageSize: 1000 }) {
			...CompetitionCardItem
		}
	}
`;

export const Anchor = gql`
	${TextBlock}
	${BildTextBlock}
	${ImageBlock}
	${VideoBlock}
	${AccordionGroup}
	${AccordionCards}
	${CarouselBlock}
	${WYSISYGBlock}
	${TeamBlock}
	${PersonBlock}
	${CardsRowBlock}
	${PodcastBlock}
	${PartnerBlock}
	${ImageGridBlock}
	${GridNavigationBlock}
	${GraphicBlock}
	${FileBlock}
	${EventsBlock}
	${ButtonLinkRowBlock}
	${CompetitionCardsBlock}

	fragment Anchor on ComponentBlocksAnchor {
		id
		__typename
		anchorType
		anchorSectionTitle
		anchorShowBorder
		anchorTabs {
			id
			__typename
			anchorTabTitle
			anchorTabContent {
				__typename
				... on PageSection {
					__typename
					documentId
					pageSectionIdentifier
					pageSectionContent {
						__typename
							... on ComponentBlocksTextBlock { ...TextBlock }
							... on ComponentBlocksBildTextBlock { ...BildTextBlock }
							... on ComponentBlocksImage { ...ImageBlock }
							... on ComponentBlocksVideoBlock { ...VideoBlock }
							... on ComponentBlocksAccordionGroup { ...AccordionGroup }
							... on ComponentBlocksAccordionCards { ...AccordionCards }
							... on ComponentBlocksCarouselBlock { ...CarouselBlock }
							... on ComponentBlocksWysiwygBlock { ...WYSISYGBlock }
							... on ComponentBlocksTeamBlock { ...TeamBlock }
							... on ComponentBlocksSectionBlock { ...PersonBlock }
							... on ComponentBlocksCardsRow { ...CardsRowBlock }
							... on ComponentBlocksPodcastBlock { ...PodcastBlock }
							... on ComponentBlocksPartnerBlock { ...PartnerBlock }
							... on ComponentBlocksImageGridBlock { ...ImageGridBlock }
							... on ComponentBlocksGridNavigation { ...GridNavigationBlock }
							... on ComponentBlocksGraphicBlock { ...GraphicBlock }
							... on ComponentBlocksFileBlock { ...FileBlock }
							... on ComponentBlocksEvents { ...EventsBlock }
							... on ComponentBlocksButtonLinkRow { ...ButtonLinkRowBlock }
							... on ComponentBlocksCompetitionCards { ...CompetitionCardsBlock }
					}
				}
			}
		}
	}
`;
